/**
 * Independent time operation tool to facilitate subsequent switch to dayjs
 */
import dayjs, { Dayjs } from 'dayjs';
import type { Recordable } from '@mts-fe/types';

/** 日期时间格式化 */
export const DAY_FORMAT = {
  Y: 'YYYY',
  M: 'YYYY/MM',
  D: 'YYYY/MM/DD',
  DT: 'YYYY/MM/DD HH:mm',
  DTS: 'YYYY/MM/DD HH:mm:ss',
  T: 'HH:mm',
  TS: 'HH:mm:ss',
  // hack
  _D: 'YYYY-MM-DD',
} as const;

export function formatToDateTime(date?: dayjs.ConfigType, format?: string): string {
  return dayjs(date).format(format || DAY_FORMAT.DT);
}

export function formatToDate(date?: dayjs.ConfigType, format?: string): string {
  return dayjs(date).format(format || DAY_FORMAT.D);
}

export const formatToTime = (t: string, format?: string) =>
  dayjs(t, [t.length <= 5 ? DAY_FORMAT.T : DAY_FORMAT.TS]).format(format || DAY_FORMAT.TS);

dayjs.locale('zh-cn', { weekStart: 1 });

export const dateUtil = dayjs;

type FORMAT_KEY = keyof typeof DAY_FORMAT;
type DateInput = Date | string | number | dayjs.Dayjs | null | undefined;
export class DateFormatter {
  static formats = DAY_FORMAT;

  private static toDate(date: DateInput): Date {
    if (date && date instanceof Date) return date;
    if (date && typeof date === 'object' && 'toDate' in date) return date.toDate();

    if (typeof date === 'number') return new Date(date);

    if (typeof date === 'string') {
      const d = new Date(date);
      if (!isNaN(d.getTime())) return d;
    }

    return new Date();
  }

  private static formatters: Record<FORMAT_KEY, (date: DateInput) => string> = {
    Y: (date) => {
      const d = this.toDate(date);
      return '' + d.getFullYear();
    },
    M: (date) => {
      const d = this.toDate(date);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      return y + '/' + (m < 10 ? '0' + m : m);
    },
    D: (date) => {
      const d = this.toDate(date);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const day = d.getDate();
      return y + '/' + (m < 10 ? '0' + m : m) + '/' + (day < 10 ? '0' + day : day);
    },
    DT: (date) => {
      const d = this.toDate(date);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const day = d.getDate();
      const h = d.getHours();
      const min = d.getMinutes();
      return (
        y +
        '/' +
        (m < 10 ? '0' + m : m) +
        '/' +
        (day < 10 ? '0' + day : day) +
        ' ' +
        (h < 10 ? '0' + h : h) +
        ':' +
        (min < 10 ? '0' + min : min)
      );
    },
    DTS: (date) => {
      const d = this.toDate(date);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const day = d.getDate();
      const h = d.getHours();
      const min = d.getMinutes();
      const s = d.getSeconds();
      return (
        y +
        '/' +
        (m < 10 ? '0' + m : m) +
        '/' +
        (day < 10 ? '0' + day : day) +
        ' ' +
        (h < 10 ? '0' + h : h) +
        ':' +
        (min < 10 ? '0' + min : min) +
        ':' +
        (s < 10 ? '0' + s : s)
      );
    },
    T: (date) => {
      const d = this.toDate(date);
      const h = d.getHours();
      const min = d.getMinutes();
      return (h < 10 ? '0' + h : h) + ':' + (min < 10 ? '0' + min : min);
    },
    TS: (date) => {
      const d = this.toDate(date);
      const h = d.getHours();
      const min = d.getMinutes();
      const s = d.getSeconds();
      return (
        (h < 10 ? '0' + h : h) + ':' + (min < 10 ? '0' + min : min) + ':' + (s < 10 ? '0' + s : s)
      );
    },
    _D: (date) => {
      const d = this.toDate(date);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const day = d.getDate();
      return y + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day);
    },
  };
  private static getFormatter(format: FORMAT_KEY | string): (date: DateInput) => string {
    if (!this.formatters[format]) {
      this.formatters[format] = this.formatters[format] || ((date) => dayjs(date).format(format));
    }
    return this.formatters[format];
  }

  static format(date: DateInput, format: FORMAT_KEY | string) {
    return this.getFormatter(format)(date);
  }

  /** 格式化为年份（YYYY） */
  static Y(date?: DateInput) {
    return this.format(date, 'Y');
  }
  /** 格式化为年月（YYYY/MM） */
  static M(date?: DateInput) {
    return this.format(date, 'M');
  }
  /** 格式化为年月日（YYYY/MM/DD） */
  static D(date?: DateInput) {
    return this.format(date, 'D');
  }
  /** 格式化为年月日时分（YYYY/MM/DD HH:mm） */
  static DT(date?: DateInput) {
    return this.format(date, 'DT');
  }
  /** 格式化为年月日时分秒（YYYY/MM/DD HH:mm:ss） */
  static DTS(date?: DateInput) {
    return this.format(date, 'DTS');
  }
  /** 格式化为时分（HH:mm） */
  static T(date?: DateInput) {
    return this.format(date, 'T');
  }
  /** 格式化为时分秒（HH:mm:ss） */
  static TS(date?: DateInput) {
    return this.format(date, 'TS');
  }
  /** @deprecated 格式化为年月日（YYYY-MM-DD） */
  static _D(date?: DateInput) {
    return this.format(date, '_D');
  }
}

/**
 * 获取指定年份的所有周数及日期范围
 * @example const weeks = getWeeksOfYear(2025);
 */
export function getWeeksOfYear(year: number) {
  let week = 1;
  const weeks: { week: number; startDate: string; endDate: string }[] = [];

  // 1. 找到当前年的第一个周一
  let firstMonday = dayjs(`${year}-01-01`);
  while (firstMonday.day() !== 1) {
    // 1 表示周一
    firstMonday = firstMonday.add(1, 'day');
  }

  // 2. 开始计算每周
  let currentStart = firstMonday;
  while (currentStart.year() <= year) {
    let currentEnd = currentStart.add(6, 'days'); // 当前周的周日

    // 3. 处理最后一周不足7天的情况
    if (currentEnd.year() > year) {
      // 需要补齐到完整7天
      currentEnd = currentStart.add(6, 'days');
    }

    weeks.push({
      week,
      startDate: currentStart.format(DAY_FORMAT.D),
      endDate: currentEnd.format(DAY_FORMAT.D),
    });

    // 4. 准备下一周
    week++;
    currentStart = currentEnd.add(1, 'day');
  }

  return weeks;
}

// 单位映射表
const UNIT_MAP: Recordable<dayjs.ManipulateType> = {
  d: 'day',
  day: 'day',
  days: 'day',
  w: 'week',
  week: 'week',
  weeks: 'week',
  m: 'month',
  month: 'month',
  months: 'month',
  y: 'year',
  year: 'year',
  years: 'year',
};

const RULE_REGEX = /^(-?\d+)([a-z]+)$/;
/**
 * 根据日期规则字符串获取日期
 * @example
 *   getDateByRule('30day')       // 30天前
 *   getDateByRule('-1month')     // 1个月后
 */
export const getDateByRule = (rule: string) => {
  const match = RULE_REGEX.exec(rule.trim().toLowerCase());
  if (!match) return console.error(`Invalid rule format: ${rule}`);

  const unitKey = match[2];
  const unit = UNIT_MAP[unitKey];
  if (!unit) return console.error(`Unsupported time unit: ${unitKey}`);

  const num = parseInt(match[1], 10);
  return dayjs()[num >= 0 ? 'add' : 'subtract'](Math.abs(num), unit);
};

/**
 * 解析日期规则字符串为对象
 * @example parseRangeRules('before:3month|after:0day')
 */
export const parseRangeRules = (rulesStr: string) => {
  const result: Recordable<Dayjs | void> = {};
  if (!rulesStr) return result;

  const parts = rulesStr.split('|');
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const colonIndex = part.indexOf(':');
    if (colonIndex === -1) continue;

    const key = part.slice(0, colonIndex);
    const value = part.slice(colonIndex + 1) || '0day';

    result[key] = getDateByRule(key === 'before' && !value.startsWith('-') ? '-' + value : value);
  }
  return result;
};

const rangeMap: Recordable<
  (day: Dayjs, unit: dayjs.ManipulateType, num?: number) => [Dayjs, Dayjs]
> = {
  getCur: (day, unit) => [day.startOf(unit), day.endOf(unit)],
  getLast: (day, unit, num = 1) => {
    const newDay = day.subtract(num, unit);
    return [newDay.startOf(unit), newDay.endOf(unit)];
  },
  // getNext: (day, unit, num = 1) => [day.startOf(unit), day.add(num, unit).endOf(unit)],
};

// 日期区间预设
const PRESET_MAP: Recordable<{
  label: string;
  unit: dayjs.ManipulateType;
  method: 'getCur' | 'getLast';
  num?: number;
}> = {
  d: { label: 'g.25120009', unit: 'day', method: 'getCur' },
  dl: { label: 'g.25120010', unit: 'day', method: 'getLast' },
  df: { label: 'g.26010001', unit: 'day', method: 'getLast', num: -1 },
  w: { label: 'g.25120011', unit: 'week', method: 'getCur' },
  wl: { label: 'g.25120012', unit: 'week', method: 'getLast' },
  m: { label: 'g.25120013', unit: 'month', method: 'getCur' },
  ml: { label: 'g.25120014', unit: 'month', method: 'getLast' },
  y: { label: 'g.25120015', unit: 'year', method: 'getCur' },
};

export interface RangePreset {
  value: [Dayjs, Dayjs];
  label: string;
  key: string;
}

let cachedPresets: RangePreset[];
/** 获取日期范围预设 */
export const getRangePresets = (tFn: (key: string) => string) => {
  if (cachedPresets) return cachedPresets;

  const now = dayjs();
  cachedPresets = Object.keys(PRESET_MAP).map((key) => {
    const { label, unit, method, num } = PRESET_MAP[key];
    return { value: rangeMap[method](now, unit, num), label: tFn(label), key };
  });
  cachedPresets.unshift({ value: ['', ''] as any, label: tFn('g.25090009'), key: 'all' });
  return cachedPresets;
};

/** 创建一个检测器，检测当前时间是否超过第二天的指定时间 */
export function createDayTimeChecker(nextDayTime: string) {
  if (!nextDayTime) return;

  const startDate = new Date();
  const nextDay = new Date();
  nextDay.setDate(startDate.getDate() + 1);

  const [hours, minutes, seconds] = nextDayTime.split(':').map(Number);
  nextDay.setHours(hours, minutes, seconds, 0);

  const targetTimestamp = nextDay.getTime();

  return () => new Date().getTime() >= targetTimestamp;
}
